import { useState, useMemo, useEffect } from 'react'
import { NavLink, Stack, Text, Box, Select, TextInput, Badge, Collapse, Tooltip, UnstyledButton, Avatar, Group } from '@mantine/core'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  IconRoute,
  IconDashboard, IconBox, IconChecklist, IconClipboardList, IconList, IconServer,
  IconChartBar,
  IconShield,
  IconShieldLock, IconUsersGroup, IconBuilding, IconKey, IconLock,
  IconSearch, IconChevronRight,
  IconReportAnalytics, IconGitPullRequest,
  IconCategory, IconTemplate, IconBell, IconBellRinging, IconSnowflake, IconMessages,
  IconPigMoney,
  IconShieldCheck,
  IconPlug,
  IconPlugConnected,
  IconSitemap,
  IconDatabase,
  IconMessageCircle,
  IconMail,
  IconBook,
  IconBinaryTree2,
  IconCertificate,
  IconScale,
  IconNetwork,
  IconApps,
  IconInbox,
  IconBuildingCommunity,
  IconShieldSearch,
  IconSettings,
  IconDatabaseSearch,
  IconLayoutDashboard,
  IconCalendarCog,
  IconHeartbeat,
  IconRobot,
  IconDeviceDesktopCog,
} from '@tabler/icons-react'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { chamadosApi, CHAVE_PENDENCIAS_DO_MENU, type InboxCounts } from '@/api/chamados'
import { permissionsApi } from '@/api/permissions'
import { organizationsApi } from '@/api/organizations'
import { platformApi } from '@/api/platform'
import { podeNaPlataforma, useAuthStore, useCurrentTenant, useCan } from '@/store/auth'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useCriteriosDeArea } from '@/hooks/useCriteriosDeArea'
import { useDisclosure } from '@mantine/hooks'
import { aiVars } from '@/theme'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { APP_VERSION } from '@/version'
import { buildStamp } from '@/buildInfo'
import { ChangelogModal } from './ChangelogModal'
import { SystemInfoButton } from '@/components/layout/SystemInfoButton'
import { BotaoNovaOrganizacao } from '@/components/organizacoes/BotaoNovaOrganizacao'

interface NavItem {
  label: string
  icon: React.ReactNode
  path?: string
  permission?: string
  hidden?: boolean
  badge?: number
  description?: string
  children?: NavItem[]
}

interface NavSection {
  title: string
  items: NavItem[]
  treeview?: boolean
}

function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === 'true' } catch { return false }
  })
  const toggle = () => {
    setCollapsed(v => {
      localStorage.setItem('sidebar_collapsed', String(!v))
      return !v
    })
  }
  return { collapsed, toggle }
}

function useSubmenuToggle() {
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({})
  const toggleSubmenu = (key: string) => {
    setOpenSubmenus(prev => ({ ...prev, [key]: !prev[key] }))
  }
  return { openSubmenus, toggleSubmenu }
}

/** Match permissivo (prefix-aware) usado pra decidir se uma rota
 * "pertence" a um item do sidebar (secao pai ou submenu).
 *
 * Diferente de isPathActive (estrita, usada pra highlight do item ativo).
 *
 * Regras:
 * - itemPath '/' → match apenas exato (senao todo path bateria)
 * - itemPath qualquer outro → location === basePath OU location.startsWith(basePath + '/')
 * - Query string do itemPath e descartada (nao usada na decisao)
 */
function pathBelongsTo(itemPath: string | undefined, locPathname: string): boolean {
  if (!itemPath) return false
  const basePath = itemPath.split('?')[0]
  if (basePath === '/') return locPathname === '/'
  return locPathname === basePath || locPathname.startsWith(basePath + '/')
}

/* Color Admin style constants */
const S = {
  bg: aiVars.sidebarBg,            // #2d353c
  deep: aiVars.sidebarDeep,        // #20252a
  text: '#a8b6bc',
  textHover: '#e9ecef',
  textActive: '#ffffff',
  accentBg: aiVars.sidebarDeep,    // #20252a — active item bg
  accentBorder: aiVars.brandVerde, // #098d93 — left indicator
  hoverBg: 'rgba(255, 255, 255, 0.04)',
  divider: '#20252a',
  menuHeader: '#adb5bd',
  badgeBg: '#20252a',
  inputBg: '#1b1f24',
  inputBorder: 'rgba(255, 255, 255, 0.08)',
  dropdownBg: '#343c44',
} as const

function isPathActive(itemPath: string | undefined, locPathname: string, locSearch: string): boolean {
  if (!itemPath) return false
  const qIdx = itemPath.indexOf('?')
  if (qIdx === -1) return locPathname === itemPath
  const pathname = itemPath.slice(0, qIdx)
  if (locPathname !== pathname) return false
  const itemParams = new URLSearchParams(itemPath.slice(qIdx + 1))
  const locParams = new URLSearchParams(locSearch)
  for (const [k, v] of itemParams) {
    if (locParams.get(k) !== v) return false
  }
  return true
}

// Valor sentinela da opção "Todas as organizações (plataforma)" no seletor de
// org ativa. Não é um id de org real — quando escolhido, aciona setGlobalScope.
const GLOBAL_SCOPE_OPTION = '__global__'

/** Badge do menu Chamados: atribuídos a mim em estado acionável MAIS o que espera
 *  a minha validação (spec 2026-09-27 §6.2). As duas contagens vêm do servidor
 *  (`/me/inbox/counts`), sem página; a soma é exata porque nenhum status está nas
 *  duas (guardado em `test_inbox_aguardando_validacao.py`). */
export function contarPendenciasDoMenu(counts: InboxCounts | undefined): number {
  if (!counts) return 0
  return (counts.assigned_actionable ?? 0) + (counts.aguardando_validacao ?? 0)
}

/** Busca do menu ignora caixa e acento: quem digita "notificacoes" acha "Notificações". */
export function normalizarBusca(texto: string): string {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
}

interface SidebarProps {
  collapsed?: boolean
  onToggle?: () => void
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, setSelectedTenant, setSelectedOrg, selectedOrgId, globalScope, setGlobalScope, previewTemplate } = useAuthStore()
  const currentTenant = useCurrentTenant()
  const qc = useQueryClient()
  const can = useCan()
  const { aws_role_coverage_scan_enabled } = useFeatureFlags()
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const { openSubmenus, toggleSubmenu } = useSubmenuToggle()
  // Accordion estrito: apenas uma secao top-level aberta por vez.
  // Resolucao: clique explicito do user (se recente) → senao secao da
  // rota atual → senao null (tudo fechado). useEffect limpa o clique
  // explicito a cada navegacao, devolvendo o controle pra rota.
  const [explicitSection, setExplicitSection] = useState<string | null>(null)
  useEffect(() => { setExplicitSection(null) }, [location.pathname])
  const [changelogOpened, { open: openChangelog, close: closeChangelog }] = useDisclosure(false)

  // TASK-2473: tenant switcher fica disabled quando estamos em /chamados* com
  // scope=global OU scope=org (nessas listagens o tenant nao filtra resultados).
  const currentScope = searchParams.get('scope') ?? 'own'
  const isInChamadosScopeMode =
    location.pathname.startsWith('/chamados') &&
    (currentScope === 'global' || currentScope === 'org')

  // TASK-2473: calcular permissoes globais (uniao globals + todos tenant_perms).
  const allPerms = useMemo(() => {
    if (!user) return new Set<string>()
    const perms = new Set<string>(user.global_permissions ?? [])
    Object.values(user.tenant_permissions ?? {}).forEach((arr) => arr.forEach((p) => perms.add(p)))
    return perms
  }, [user])
  // Escopo global "Todas as organizações (plataforma)" (fase 1). Gate transversal:
  // espelha rbac.can_use_global_scope (admin:full ou plataforma:global_view).
  const canUseGlobalScope = allPerms.has('admin:full') || allPerms.has('plataforma:global_view')

  const seenVersion = (() => {
    try { return localStorage.getItem('consoleai_seen_version') } catch { return null }
  })()
  const isNewVersion = seenVersion !== APP_VERSION

  const handleOpenChangelog = () => {
    openChangelog()
    try { localStorage.setItem('consoleai_seen_version', APP_VERSION) } catch { /* noop */ }
  }

  const { data: badgeCounts } = useQuery({
    queryKey: ['sidebar-badges', currentTenant?.id],
    queryFn: async () => ({ chamados: 0, alertas: 0 }),
    staleTime: 60_000,
    enabled: !!currentTenant,
  })

  // Badge do menu Chamados = chamados que EU preciso atuar = atribuídos a mim
  // em estado ACIONÁVEL (em triagem / em atendimento / reaberto) MAIS o que
  // espera a minha validação (Onda 3) — `contarPendenciasDoMenu`. Antes o badge
  // era o total do inbox cross-tenant (time + atribuídos + aguardando), pouco
  // acionável. Zero → sem badge. Contagem do servidor, com chave própria: a
  // antiga dividia `chamados-inbox-count` com o `getInbox({ limit: 5 })` do Header.
  const { data: inboxCountData } = useQuery({
    queryKey: CHAVE_PENDENCIAS_DO_MENU,
    queryFn: async () => (await chamadosApi.getInboxCounts()).data,
    staleTime: 60_000,
    enabled: !!user,
  })
  const myActionableCount = contarPendenciasDoMenu(inboxCountData)

  // Critérios de visibilidade decididos por consulta ou por posse (Dashboard, Base de
  // Conhecimento, Meu robô, Pedidos de análise, Organizações, Gateways WhatsApp, Mensagens
  // do sistema): vivem em `useCriteriosDeArea`, que o filtro de novidades também usa — a
  // explicação de cada regra está lá. Menu é conveniência; a rota segue sendo a autoridade.
  const { criterios } = useCriteriosDeArea()
  const hasDashboardAccess = criterios.dashboard
  const podeVerKb = criterios.baseDeConhecimento
  const canSeeOrganizacoes = criterios.organizacoes
  const podeVerGatewaysWhatsApp = criterios.gatewaysWhatsapp
  const canSeeMensagensSistema = criterios.mensagensDoSistema
  const meuRobo = criterios.meuRobo
  const temPedidos = criterios.pedidosDeAnalise
  const podeVerConversasWhatsApi = criterios.conversasWhatsapi

  const SECTIONS: NavSection[] = [
    {
      title: 'Operacional',
      treeview: true,
      items: [
        { label: 'Dashboard',  icon: <IconDashboard size={18} />,     path: '/', hidden: !hasDashboardAccess },
        { label: 'Aplicativos', icon: <IconApps size={18} />,         path: '/aplicativos' },
        { label: 'Base de Conhecimento', icon: <IconDatabaseSearch size={18} />, path: '/conhecimento', hidden: !podeVerKb },
        { label: 'Recursos',   icon: <IconBox size={18} />,           path: '/recursos',  permission: 'recursos:read' },
        { label: 'Servidores', icon: <IconServer size={18} />,        path: '/ssm',       permission: 'ssm:session:dev:access' },
        {
          label: 'Chamados',
          icon: <IconChecklist size={18} />,
          permission: 'chamados:read',
          // Badge = chamados atribuídos a mim em estado acionável; 0 → sem badge.
          badge: myActionableCount > 0 ? myActionableCount : undefined,
          // Entrada e um item de consulta. "Meus chamados", "Organização" e "Todos" abriam a
          // mesma página mudando só o ?scope; viraram "Todos os chamados", e o escopo se
          // escolhe na página pelo useScope (spec 2026-10-21-chamados-menu-todos-os-chamados).
          // Antes já tinham saído "Minha fila" e "Fila da Organização" (spec 2026-09-27 §7, D4).
          children: [
            { label: 'Entrada',       icon: <IconInbox size={16} />,           path: '/chamados/inbox',          description: 'Chamados atribuídos a você ou ao seu time — o que precisa da sua ação.' },
            { label: 'Todos os chamados', icon: <IconList size={16} />, path: '/chamados', description: 'Consultar chamados, abertos e encerrados: os seus, os da sua organização ou os da plataforma, conforme o seu acesso.' },
            { label: 'GMUD',       icon: <IconGitPullRequest size={16} />,  path: '/gmud', permission: 'chamados:gmud_read' },
            // Fatia 4c: a tela existia desde a Fatia 1 e não estava em menu
            // nenhum — só chegava quem digitasse a URL. Fica ao lado da GMUD
            // porque é o cadastro de quem aprova a GMUD; separá-la em
            // Administração esconderia a relação. `ccm:manage` filtra: quem não
            // tem, não vê o item.
            { label: 'Comitês de Mudança', icon: <IconUsersGroup size={16} />, path: '/admin/ccm/comites', permission: 'ccm:manage', description: 'Quem aprova mudança em cada organização: membros, quórum e cobertura.' },
            { label: 'Dashboard', icon: <IconChartBar size={16} />, path: '/chamados/relatorios', description: 'Indicadores de chamados e mudanças (SLA, volume, tempos).' },
            { label: 'Como funciona', icon: <IconRoute size={16} />, path: '/chamados/como-funciona', description: 'Por onde um chamado passa até fechar, quem move cada passo e como o prazo corre.' },
            // Mesmo gate da página (RelatorioMudancas exige gmud_read) e da GMUD acima:
            // menu e página gateados juntos, como em Integrações › WhatsApi › Documentação.
            // Sem isto o item aparece para quem só encontra o Alert de recusa ao clicar.
            { label: 'Relatório de Mudanças', icon: <IconClipboardList size={16} />, path: '/chamados/relatorio-mudancas', permission: 'chamados:gmud_read' },
          ],
        },
        { label: 'Banco de Dados', icon: <IconDatabase size={18} />, path: '/database', permission: 'rds:read' },
        { label: 'Endereçamento IP', icon: <IconNetwork size={18} />, path: '/enderecamento', permission: 'ipam:view', description: 'Onde estão os ambientes de endereçamento IP de verdade, conciliados contra o registro.' },
        { label: 'Rotinas', icon: <IconCalendarCog size={18} />, path: '/rotinas', permission: 'routines:manage' },
        { label: 'Notificações', icon: <IconBell size={18} />, path: '/notificacoes' },
        { label: 'Alertas', icon: <IconBellRinging size={18} />, path: '/alertas' },
        { label: 'Conversas', icon: <IconMessages size={18} />, path: '/chat' },
        {
          label: 'Auditoria',
          icon: <IconClipboardList size={18} />,
          permission: 'audit:read',
          children: [
            { label: 'Eventos',   icon: <IconClipboardList size={16} />, path: '/auditoria' },
            { label: 'Dashboard', icon: <IconChartBar size={16} />,      path: '/auditoria/dashboard' },
            { label: 'Políticas', icon: <IconScale size={16} />,         path: '/auditoria/politicas' },
            // `certificacoes:read` é permissão de PLATAFORMA: o backend pergunta com
            // `tenant_id=None`, onde o cap por ambiente nem se aplica. O campo
            // `permission` resolve pelo `can()`, escopado ao tenant do seletor, e por
            // isso o item sumia do menu do perfil AUDITOR enquanto a rota abria
            // normalmente por URL direta (30/09/2026). Mesmo padrão de Frota.
            { label: 'Certificações', icon: <IconCertificate size={16} />, path: '/admin/certificacoes', hidden: !podeNaPlataforma(user, 'certificacoes', 'read'), description: 'Certificados externos, o escopo transcrito do documento e o selo de cada ambiente.' },
            ...(user?.roles?.some(r => r.code === 'auditor_global')
              ? [{ label: 'Global', icon: <IconShieldLock size={16} />, path: '/auditoria-global' }]
              : []),
          ],
        },
      ],
    },
    {
      title: 'FinOps',
      treeview: true,
      items: [
        { label: 'FinOps',           icon: <IconDashboard size={18} />,     path: '/finops',               permission: 'finops:read' },
        { label: 'Recomendações',    icon: <IconPigMoney size={18} />,     path: '/finops/recomendacoes', permission: 'finops:read' },
        { label: 'Alertas FinOps',   icon: <IconBell size={18} />,          path: '/finops/alertas-v2',    permission: 'finops:read' },
        { label: 'Relatórios',       icon: <IconReportAnalytics size={18} />, path: '/finops/relatorios-v2', permission: 'admin:platform' },
        { label: 'Por Organização',  icon: <IconReportAnalytics size={18} />, path: '/finops/relatorios/organizacao', permission: 'finops:read' },
        { label: 'Assinaturas',      icon: <IconMail size={18} />,            path: '/finops/relatorios/assinaturas', permission: 'finops:export' },
      ],
    },
    {
      title: 'Segurança',
      treeview: true,
      items: [
        { label: 'Security Dashboard', icon: <IconShieldCheck size={18} />, path: '/security', permission: 'security:read' },
        {
          label: 'WAF',
          icon: <IconShield size={18} />,
          permission: 'waf:view',
          children: [
            { label: 'Visão geral', icon: <IconShield size={16} />, path: '/security/waf', permission: 'waf:view' },
            { label: 'Dashboard', icon: <IconLayoutDashboard size={16} />, path: '/security/waf-dashboard', permission: 'waf:view' },
          ],
        },
        {
          label: 'ZTNA',
          icon: <IconShieldCheck size={18} />,
          children: [
            { label: 'Onboarding',  icon: <IconShieldCheck size={16} />, path: '/admin/ztna',                permission: 'ztna:onboard:provision' },
            { label: 'Conectores',  icon: <IconNetwork size={16} />,     path: '/admin/connectors',          permission: 'connectors:manage' },
            { label: 'Sessões',     icon: <IconShieldLock size={16} />,  path: '/admin/sessions',            permission: 'broker:revoke_session' },
            { label: 'Clientes',    icon: <IconUsersGroup size={16} />,  path: '/admin/ztna/customers',      permission: 'ztna_customer:read' },
          ],
        },
        { label: 'CloudHSM', icon: <IconLock size={18} />, path: '/security/cloudhsm', permission: 'cloudhsm:view' },
      ],
    },
    {
      title: 'Integrações',
      treeview: true,
      items: [
        {
          label: 'Jira',
          icon: <IconPlug size={18} />,
          permission: 'jira:read',
          children: [
            { label: 'Dashboard', icon: <IconPlug size={16} />, path: '/integracoes/jira', permission: 'jira:read' },
            { label: 'Configuração', icon: <IconPlug size={16} />, path: '/integracoes/jira/config', permission: 'jira:admin' },
          ],
        },
        {
          label: 'WhatsApi',
          icon: <IconMessageCircle size={18} />,
          permission: 'whatsapi:read',
          children: [
            { label: 'Mensagens',     icon: <IconMessageCircle size={16} />, path: '/whatsapi/messages',  permission: 'whatsapi:read' },
            // Só no modo organização (spec 2026-11-02 conversas §5.7): some sem `whatsapi:read` em organização.
            { label: 'Conversas',     icon: <IconMessages size={16} />,      path: '/whatsapi/conversas', hidden: !podeVerConversasWhatsApi },
            { label: 'Estatísticas',  icon: <IconChartBar size={16} />,      path: '/whatsapi/stats',     permission: 'whatsapi:observe' },
            { label: 'API Keys',      icon: <IconKey size={16} />,           path: '/whatsapi/api-keys',  permission: 'whatsapi:read' },
            { label: 'Documentação',  icon: <IconBook size={16} />,          path: '/whatsapi/docs',      permission: 'whatsapi:read' },
          ],
        },
      ],
    },
    {
      title: 'Robô',
      treeview: true,
      items: [
        { label: 'Meu robô', icon: <IconRobot size={18} />, path: '/robo/meu', hidden: !meuRobo },
        // Sem `permission`: ela avaliaria no tenant do seletor. A frota e' da
        // plataforma (spec 2026-09-24-robo-sem-cliente, D1): `robo:read` GLOBAL.
        { label: 'Frota', icon: <IconDeviceDesktopCog size={18} />, path: '/robo/frota', hidden: !podeNaPlataforma(user, 'robo', 'read') },
        { label: 'Pedidos de análise', icon: <IconInbox size={18} />, path: '/robo/pedidos', hidden: !temPedidos },
      ],
    },
    {
      title: 'Administração',
      treeview: true,
      items: [
        { label: 'Integrações',        icon: <IconPlugConnected size={18} />, path: '/admin/integracoes',   permission: 'usuarios:manage' },
        { label: 'Usuários', icon: <IconUsersGroup size={18} />, path: '/admin/usuarios',      permission: 'usuarios:manage' },
        { label: 'Tenants',            icon: <IconBuilding size={18} />,   path: '/admin/tenants',       permission: 'admin:platform' },
        // Visível para admin OU gestor/responsável de alguma org (Fase 1 —
        // gestão de colaboradores). Gate real é do backend (_can_manage_org).
        { label: 'Organizações',       icon: <IconSitemap size={18} />,    path: '/admin/organizacoes',  hidden: !canSeeOrganizacoes },
        { label: 'Gateways WhatsApp',  icon: <IconMessageCircle size={18} />, path: '/admin/whatsapp-gateways', hidden: !podeVerGatewaysWhatsApp },
        { label: 'Permissões',         icon: <IconKey size={18} />,        path: '/admin/permissoes',    permission: 'usuarios:manage' },
        { label: 'SSO',                icon: <IconKey size={18} />,        path: '/admin/conexoes-sso',  hidden: !podeNaPlataforma(user, 'admin', 'platform') },
        { label: 'Funções',            icon: <IconShieldCheck size={18} />, path: '/admin/roles',        permission: 'admin:platform' },
        { label: 'Configurações',      icon: <IconSettings size={18} />,      path: '/admin/configuracoes', permission: 'admin:platform' },
        { label: 'Cobertura de Acesso AWS', icon: <IconShieldSearch size={18} />, path: '/admin/aws-access', permission: 'admin:platform', hidden: !aws_role_coverage_scan_enabled },
        { label: 'Diretório LDAP',     icon: <IconUsersGroup size={18} />, path: '/admin/ldap',          permission: 'ldap:manage' },
        { label: 'Times de alerta',    icon: <IconBell size={18} />,        path: '/admin/alert-teams',   permission: 'alerts:manage' },
        { label: 'Fontes de heartbeat', icon: <IconHeartbeat size={18} />, path: '/admin/heartbeat-sources', permission: 'alerts:manage' },
        { label: 'Mensagens do sistema', icon: <IconMail size={18} />,      path: '/admin/notificacoes',  hidden: !canSeeMensagensSistema },
        {
          label: 'Chamados',
          icon: <IconChecklist size={18} />,
          permission: 'admin:platform',
          children: [
            { label: 'Categorias',       icon: <IconCategory size={16} />,  path: '/admin/chamados/categorias' },
            { label: 'Templates',        icon: <IconTemplate size={16} />,  path: '/admin/chamados/templates' },
            { label: 'Regras de Alerta', icon: <IconBell size={16} />,      path: '/admin/chamados/alertas' },
            { label: 'Freeze Periods',   icon: <IconSnowflake size={16} />, path: '/admin/gmud/freeze' },
          ],
        },
      ],
    },
  ]

  // Detalhe da org ativa (members → tenants) p/ estreitar a lista de tenants.
  // org_get (GET /organizations/{id}) é bearer-only → funciona p/ não-admin tb.
  const { data: activeOrgDetail } = useQuery({
    queryKey: ['organization', selectedOrgId],
    queryFn: () => organizationsApi.get(selectedOrgId as number),
    enabled: selectedOrgId != null,
  })

  const sourceTenants = previewTemplate?.tenants.length
    ? previewTemplate.tenants
    : (user?.tenants ?? [])

  // Escopo global (fase 2): sob globalScope, o seletor lista TODOS os tenants da
  // plataforma (não só os do usuário). Só busca para quem pode usar o escopo global.
  const { data: globalTenants = [] } = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: () => platformApi.listAllTenants(),
    enabled: globalScope && canUseGlobalScope,
  })

  // Org ativa estreita a lista aos tenants daquela org (interseção com os
  // acessíveis). Sem org (modo união) ou em preview de template → lista completa.
  const orgTenantIds = useMemo(() => {
    if (selectedOrgId == null || previewTemplate || !activeOrgDetail?.members) return null
    return new Set(activeOrgDetail.members.map((m) => m.tenant_id))
  }, [selectedOrgId, previewTemplate, activeOrgDetail])

  const visibleTenants = orgTenantIds
    ? sourceTenants.filter((t) => orgTenantIds.has(t.id))
    : sourceTenants

  const tenantData = globalScope
    ? // Fonte global: nome · org · ambiente (diferencia homônimos entre orgs).
      // Dedupe por id (defesa: um tenant em 2+ orgs não pode virar opção duplicada
      // no Select — Mantine lança "Duplicate options").
      Array.from(new Map(globalTenants.map((t) => [t.id, t])).values())
        .sort((a, b) =>
          a.name.localeCompare(b.name, 'pt-BR') ||
          (a.environment ?? '').localeCompare(b.environment ?? '', 'pt-BR'))
        .map((t) => ({
          value: String(t.id),
          label: [t.name, t.organization, t.environment ? t.environment.toUpperCase() : null]
            .filter(Boolean)
            .join(' · '),
        }))
    : [...visibleTenants]
        // Ordena pelo NOME do tenant (ignora o prefixo [AMBIENTE]); desempata por ambiente.
        .sort((a, b) =>
          a.name.localeCompare(b.name, 'pt-BR') ||
          a.environment.localeCompare(b.environment, 'pt-BR'))
        .map(t => ({
          value: String(t.id),
          label: `[${t.environment.toUpperCase()}] ${t.name}`,
        }))

  // Reconciliação (estrito + auto-seleção): ao trocar de org, se o tenant ativo
  // não pertence à org selecionada, auto-seleciona o 1º tenant dela (ordem da lista).
  useEffect(() => {
    if (orgTenantIds == null) return                                 // união/preview/carregando
    if (currentTenant && orgTenantIds.has(currentTenant.id)) return  // ativo já válido
    if (tenantData.length === 0) return                              // org sem tenant acessível
    setSelectedTenant(Number(tenantData[0].value))
    qc.invalidateQueries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgTenantIds, currentTenant?.id])

  const handleTenantChange = (value: string | null) => {
    if (!value) return
    setSelectedTenant(Number(value))
    qc.invalidateQueries()
  }

  // Org de contexto (per-org-template §3/§5): seletor de organização ativa.
  // Lista as orgs visíveis ao usuário (/organizations/lite). A seleção é
  // enviada como header x-consoleai-org-id; null = sem org (união no backend).
  const { data: orgsLite = [] } = useQuery({
    queryKey: ['organizations-lite'],
    queryFn: () => permissionsApi.listOrganizationsLite(),
    enabled: !!user,
  })
  const orgData = orgsLite
    .map((o) => ({ value: String(o.id), label: o.name }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
  // Logo por org (value → logo_url) para o renderOption do seletor.
  const orgLogoByValue = useMemo(() => {
    const m = new Map<string, string>()
    orgsLite.forEach((o) => { if (o.logo_url) m.set(String(o.id), o.logo_url) })
    return m
  }, [orgsLite])
  const handleOrgChange = (value: string | null) => {
    // Sentinela do escopo global "Todas as organizações (plataforma)" (fase 1).
    if (value === GLOBAL_SCOPE_OPTION) {
      setGlobalScope(true)   // limpa selectedOrgId no store
      qc.invalidateQueries()
      return
    }
    // Limpar a seleção volta para o modo união (org_id = None no backend).
    // setSelectedOrg(!=null) também limpa globalScope no store.
    setSelectedOrg(value ? Number(value) : null)
    qc.invalidateQueries()
  }
  // Opção sentinela do seletor: só aparece para quem pode usar o escopo global.
  const orgSelectData = canUseGlobalScope
    ? [{ value: GLOBAL_SCOPE_OPTION, label: '🌐 Todas as organizações (plataforma)' }, ...orgData]
    : orgData
  const orgSelectValue = globalScope
    ? GLOBAL_SCOPE_OPTION
    : (selectedOrgId != null ? String(selectedOrgId) : null)

  const isVisible = (item: NavItem) => {
    if (item.hidden) return false
    if (!item.permission) return true
    // Split on LAST colon: permissions like 'ztna:onboard:provision' need
    // resource='ztna:onboard' + action='provision'.
    const idx = item.permission.lastIndexOf(':')
    const resource = idx >= 0 ? item.permission.slice(0, idx) : item.permission
    const action = idx >= 0 ? item.permission.slice(idx + 1) : ''
    return can(resource, action)
  }

  const filteredSections = useMemo(() => {
    const withRbacChildren = SECTIONS.map(section => ({
      ...section,
      items: section.items
        .map(item =>
          item.children
            ? { ...item, children: item.children.filter(isVisible) }
            : item
        )
        // Drop parent-only items (no own path) when RBAC filtered all children
        .filter(item => !item.children || item.path || item.children.length > 0),
    }))
    if (!search.trim()) return withRbacChildren
    const q = normalizarBusca(search)
    return withRbacChildren.map(section => ({
      ...section,
      items: section.items.map(item => {
        if (item.children) {
          const matchedChildren = item.children.filter(c => normalizarBusca(c.label).includes(q))
          if (matchedChildren.length > 0 || normalizarBusca(item.label).includes(q)) {
            return { ...item, children: matchedChildren.length > 0 ? matchedChildren : item.children }
          }
          return null
        }
        return normalizarBusca(item.label).includes(q) ? item : null
      }).filter(Boolean) as NavItem[],
    })).filter(s => s.items.length > 0)
  }, [search, SECTIONS])

  // Qual secao top-level "possui" a rota atual? Match permissivo via
  // pathBelongsTo — /chamados/novo continua mapeando pra Operacional
  // mesmo nao tendo entrada explicita no sidebar.
  const routeSection = useMemo<string | null>(() => {
    for (const section of SECTIONS) {
      for (const item of section.items) {
        const candidatePaths: string[] = []
        if (item.path) candidatePaths.push(item.path)
        if (item.children) {
          for (const c of item.children) if (c.path) candidatePaths.push(c.path)
        }
        if (candidatePaths.some(p => pathBelongsTo(p, location.pathname))) {
          return section.title
        }
      }
    }
    return null
  }, [location.pathname, SECTIONS])

  const activeSection = explicitSection ?? routeSection

  const toggleSection = (title: string) => {
    if (activeSection === title) return  // no-op pra acordeao estrito
    setExplicitSection(title)
  }

  const sidebarWidth = collapsed ? 56 : 260

  return (
    <Box
      style={{
        width: sidebarWidth,
        height: '100%',
        background: S.bg,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'width var(--ai-transition-base)',
        overflow: 'hidden',
      }}
    >
      {/* ── Brand ── */}
      <Box
        onClick={() => navigate('/')}
        style={{
          padding: collapsed ? '16px 12px' : '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          minHeight: 56,
          borderBottom: `1px solid ${S.divider}`,
          cursor: 'pointer',
        }}
      >
        {collapsed ? (
          <BrandLogo variant="symbol" color="white" width={28} />
        ) : (
          <BrandLogo variant="horizontal" color="white" width={155} />
        )}
      </Box>

      {/* ── Organização ativa (per-org-template §3) ── */}
      {/* Seletor de org de contexto: define qual perfil por org resolve as
          permissões. Vazio = união de todas as orgs do usuário (fallback). */}
      {!collapsed && (orgData.length > 0 || canUseGlobalScope) && (
        <Box style={{ padding: '12px 15px 6px' }} data-sensitive="true">
          <Text
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.6,
              color: S.menuHeader,
              textTransform: 'uppercase',
              marginBottom: 5,
            }}
          >
            Organização ativa
          </Text>
          <Tooltip
            label="Organização ativa: define o perfil que resolve suas permissões e filtra os tenants exibidos abaixo. Sem seleção = união de todas (mostra todos os seus tenants)."
            withArrow
            position="bottom"
            multiline
            w={260}
          >
            <div>
            <Select
              size="xs"
              searchable
              clearable
              value={orgSelectValue}
              onChange={handleOrgChange}
              data={orgSelectData}
              renderOption={({ option }) => {
                const logo = orgLogoByValue.get(option.value)
                return (
                  <Group gap={7} wrap="nowrap">
                    {option.value !== GLOBAL_SCOPE_OPTION && (
                      <Avatar src={logo} size="sm" radius="sm" color="teal">
                        <IconBuildingCommunity size={12} />
                      </Avatar>
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{option.label}</span>
                  </Group>
                )
              }}
              leftSection={
                selectedOrgId != null && activeOrgDetail?.logo_url
                  ? <Avatar src={activeOrgDetail.logo_url} size={16} radius="sm" />
                  : <IconBuildingCommunity size={14} style={{ color: S.accentBorder }} />
              }
              placeholder="Todas as organizações"
              styles={{
                input: {
                  backgroundColor: S.inputBg,
                  border: `1px solid ${S.inputBorder}`,
                  color: '#e9ecef',
                  fontWeight: 600,
                  fontSize: 11,
                  borderRadius: 4,
                  height: 30,
                },
                dropdown: {
                  backgroundColor: S.dropdownBg,
                  border: `1px solid ${S.inputBorder}`,
                  borderRadius: 4,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.40)',
                  padding: 3,
                },
                option: {
                  fontSize: 11,
                  color: S.text,
                  borderRadius: 3,
                  padding: '7px 10px',
                  '&[data-combobox-selected]': {
                    backgroundColor: S.accentBorder,
                    color: '#fff',
                  },
                  '&[data-combobox-active]': {
                    backgroundColor: S.hoverBg,
                    color: '#fff',
                  },
                },
              }}
            />
            </div>
          </Tooltip>
          {globalScope && (
            <Badge color="orange" size="xs" mt={6} variant="filled">
              Global — todas as orgs
            </Badge>
          )}
        </Box>
      )}

      {/* "+ Nova Organização" (spec 2026-10-27 §4.4): o servidor diz se aparece, pela
          disponibilidade do tenant atual. */}
      {!collapsed && <BotaoNovaOrganizacao tenantId={currentTenant?.id ?? null} />}

      {/* ── Tenant selector ── */}
      {/* TASK-2473: data-sensitive=true => o capturador de screenshot
          (html2canvas em ReportScreenModal) ignora este wrapper, evitando
          vazar nome de tenant em prints anexados ao chamado. */}
      {!collapsed && tenantData.length > 0 && (
        <Box style={{ padding: '10px 15px 4px' }} data-sensitive="true">
          <Text
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.6,
              color: S.menuHeader,
              textTransform: 'uppercase',
              marginBottom: 5,
            }}
          >
            Tenant
          </Text>
          <Tooltip
            label="Tenant fixo: a listagem de chamados em escopo Organização/Global ignora este filtro"
            disabled={!isInChamadosScopeMode}
            withArrow
            position="bottom"
          >
            <div>
            <Select
              size="xs"
              searchable
              value={String(currentTenant?.id ?? '')}
              onChange={handleTenantChange}
              data={tenantData}
              disabled={isInChamadosScopeMode}
              leftSection={<IconBuilding size={13} style={{ opacity: 0.5 }} />}
              placeholder="Selecionar tenant"
              styles={{
              input: {
                backgroundColor: S.inputBg,
                border: `1px solid ${S.inputBorder}`,
                color: '#e9ecef',
                fontWeight: 600,
                fontSize: 11,
                borderRadius: 4,
                height: 30,
              },
              dropdown: {
                backgroundColor: S.dropdownBg,
                border: `1px solid ${S.inputBorder}`,
                borderRadius: 4,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.40)',
                padding: 3,
              },
              option: {
                fontSize: 11,
                color: S.text,
                borderRadius: 3,
                padding: '7px 10px',
                '&[data-combobox-selected]': {
                  backgroundColor: S.accentBorder,
                  color: '#fff',
                },
                '&[data-combobox-active]': {
                  backgroundColor: S.hoverBg,
                  color: '#fff',
                },
              },
            }}
            />
            </div>
          </Tooltip>
        </Box>
      )}

      {/* ── Search ── */}
      {!collapsed && (
        <Box style={{ padding: '8px 15px 4px' }}>
          <TextInput
            size="xs"
            placeholder="Buscar..."
            leftSection={<IconSearch size={13} style={{ opacity: searchFocused ? 0.7 : 0.35 }} />}
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            name="sidebar-search"
            autoComplete="off"
            styles={{
              input: {
                backgroundColor: searchFocused ? S.inputBg : 'transparent',
                border: searchFocused ? `1px solid ${S.inputBorder}` : '1px solid transparent',
                color: '#e9ecef',
                fontSize: 11,
                borderRadius: 4,
                height: 30,
                transition: 'all var(--ai-transition-base)',
                '&::placeholder': { color: '#6c757d' },
              },
            }}
          />
        </Box>
      )}

      {/* ── Navigation ── */}
      <Box
        className="sidebar-scroll"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: collapsed ? '8px 6px' : '4px 0',
          marginTop: 4,
        }}
      >
        {filteredSections.map((section, sectionIdx) => {
          const visibleItems = section.items.filter(isVisible)
          if (visibleItems.length === 0) return null

          // Acordeao estrito: somente a secao "ativa" (clique explicito
          // OU rota atual) fica aberta. Search ativa expande todas.
          const isOpen = search.trim() ? true : section.title === activeSection

          if (collapsed) {
            const flatItems = visibleItems.flatMap(item =>
              item.children ? item.children : [item]
            )
            return (
              <Box key={section.title} mb={4}>
                {sectionIdx > 0 && (
                  <Box style={{ height: 1, background: S.divider, margin: '4px 8px 8px' }} />
                )}
                <Stack gap={1}>
                  {flatItems.map((item) => {
                    const active = isPathActive(item.path, location.pathname, location.search)
                    return (
                      <Tooltip key={item.path} label={item.label} position="right" withArrow>
                        <Box
                          onClick={() => item.path && navigate(item.path)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 36,
                            borderRadius: 4,
                            cursor: 'pointer',
                            color: active ? S.textActive : S.text,
                            backgroundColor: active ? S.accentBg : 'transparent',
                            transition: 'all var(--ai-transition-fast)',
                          }}
                          onMouseEnter={e => {
                            if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = S.hoverBg
                          }}
                          onMouseLeave={e => {
                            if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'
                          }}
                        >
                          {item.icon}
                        </Box>
                      </Tooltip>
                    )
                  })}
                </Stack>
              </Box>
            )
          }

          return (
            <Box key={section.title} mb={0}>
              {/* Section divider */}
              {sectionIdx > 0 && (
                <Box style={{ height: 1, background: S.divider, margin: '6px 0 8px' }} />
              )}

              {/* Section header — Color Admin style */}
              <UnstyledButton
                onClick={() => toggleSection(section.title)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 20px 4px',
                  width: '100%',
                  cursor: 'pointer',
                }}
              >
                <Text
                  fw={600}
                  tt="uppercase"
                  style={{
                    color: S.menuHeader,
                    letterSpacing: '0.04em',
                    fontSize: 11,
                    lineHeight: '20px',
                  }}
                >
                  {section.title}
                </Text>
                <IconChevronRight
                  size={10}
                  style={{
                    color: '#6c757d',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform var(--ai-transition-base)',
                  }}
                />
              </UnstyledButton>

              <Collapse in={isOpen}>
                <Stack gap={0}>
                  {visibleItems.map((item) => {
                    if (item.children) {
                      const submenuKey = `${section.title}::${item.label}`
                      // Match permissivo: /chamados/novo deve manter
                      // submenu "Chamados" aberto mesmo nao tendo entrada
                      // proria — pertence ao prefixo /chamados.
                      const hasActiveSubmenuChild = item.children.some(c => pathBelongsTo(c.path, location.pathname))
                      const isSubmenuOpen = search.trim() ? true : (openSubmenus[submenuKey] ?? hasActiveSubmenuChild)

                      return (
                        <Box key={submenuKey}>
                          <NavLink
                            label={
                              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <span>{item.label}</span>
                                {item.badge && item.badge > 0 ? (
                                  <Badge size="xs" variant="filled" color="red" radius="sm" styles={{ root: { fontSize: 10, height: 18 } }}>
                                    {item.badge}
                                  </Badge>
                                ) : null}
                              </Box>
                            }
                            leftSection={item.icon}
                            rightSection={
                              <IconChevronRight
                                size={10}
                                style={{
                                  color: '#6c757d',
                                  transform: isSubmenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                  transition: 'transform var(--ai-transition-base)',
                                }}
                              />
                            }
                            active={hasActiveSubmenuChild}
                            onClick={() => toggleSubmenu(submenuKey)}
                            styles={{
                              root: {
                                color: hasActiveSubmenuChild ? S.textActive : S.text,
                                backgroundColor: hasActiveSubmenuChild ? S.accentBg : 'transparent',
                                borderRadius: 0,
                                padding: '8px 20px',
                                borderLeft: hasActiveSubmenuChild ? `3px solid ${S.accentBorder}` : '3px solid transparent',
                                transition: 'all var(--ai-transition-fast)',
                                '&:hover': {
                                  backgroundColor: hasActiveSubmenuChild ? S.accentBg : S.hoverBg,
                                  color: S.textHover,
                                },
                              },
                              label: { fontSize: 13, fontWeight: 600 },
                            }}
                          />
                          <Collapse in={isSubmenuOpen}>
                            <Stack gap={0} style={{ background: S.deep }}>
                              {item.children.map((child) => {
                                const childActive = isPathActive(child.path, location.pathname, location.search)
                                const childDesc = 'description' in child ? child.description : undefined
                                const navLink = (
                                  <NavLink
                                    key={child.path}
                                    label={child.label}
                                    leftSection={
                                      <Box
                                        style={{
                                          width: 5,
                                          height: 5,
                                          borderRadius: '50%',
                                          background: childActive ? S.accentBorder : 'rgba(108, 117, 125, 0.4)',
                                          transition: 'background var(--ai-transition-fast)',
                                        }}
                                      />
                                    }
                                    active={childActive}
                                    onClick={() => navigate(child.path!)}
                                    styles={{
                                      root: {
                                        color: childActive ? S.textActive : '#6c757d',
                                        backgroundColor: 'transparent',
                                        borderRadius: 0,
                                        padding: '6px 20px 6px 44px',
                                        transition: 'all var(--ai-transition-fast)',
                                        '&:hover': {
                                          backgroundColor: 'transparent',
                                          color: S.textHover,
                                        },
                                      },
                                      label: { fontSize: 12, fontWeight: 600 },
                                    }}
                                  />
                                )
                                return childDesc ? (
                                  <Tooltip
                                    key={child.path}
                                    label={childDesc}
                                    position="right"
                                    withArrow
                                    multiline
                                    w={230}
                                    openDelay={350}
                                  >
                                    {navLink}
                                  </Tooltip>
                                ) : navLink
                              })}
                            </Stack>
                          </Collapse>
                        </Box>
                      )
                    }

                    const active = isPathActive(item.path, location.pathname, location.search)
                    return (
                      <NavLink
                        key={item.path}
                        label={
                          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <span>{item.label}</span>
                            {item.badge && item.badge > 0 ? (
                              <Badge size="xs" variant="filled" color="red" radius="sm" styles={{ root: { fontSize: 10, height: 18 } }}>
                                {item.badge}
                              </Badge>
                            ) : null}
                          </Box>
                        }
                        leftSection={item.icon}
                        active={active}
                        onClick={() => navigate(item.path!)}
                        styles={{
                          root: {
                            color: active ? S.textActive : S.text,
                            backgroundColor: active ? S.accentBg : 'transparent',
                            borderRadius: 0,
                            padding: '8px 20px',
                            borderLeft: active ? `3px solid ${S.accentBorder}` : '3px solid transparent',
                            transition: 'all var(--ai-transition-fast)',
                            '&:hover': {
                              backgroundColor: active ? S.accentBg : S.hoverBg,
                              color: S.textHover,
                            },
                          },
                          label: { fontSize: 13, fontWeight: 600 },
                        }}
                      />
                    )
                  })}
                </Stack>
              </Collapse>
            </Box>
          )
        })}
      </Box>

      {/* ── Version ── */}
      {!collapsed && (
        <Box
          onClick={handleOpenChangelog}
          style={{
            padding: '8px 20px 12px',
            borderTop: `1px solid ${S.divider}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            transition: 'opacity var(--ai-transition-fast)',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.8' }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1' }}
        >
          <Text size="xs" fw={500} style={{ color: '#6c757d', fontSize: 10 }} title={buildStamp()}>
            v{APP_VERSION}
          </Text>
          {isNewVersion && (
            <Badge size="xs" variant="filled" color="teal" styles={{ root: { fontSize: 8, height: 14, padding: '0 4px' } }}>
              NEW
            </Badge>
          )}
          <SystemInfoButton />
        </Box>
      )}

      <ChangelogModal opened={changelogOpened} onClose={closeChangelog} />

    </Box>
  )
}

export { useSidebarCollapsed }
